/**
 * The BowlineWallet component
 */
import { Balance } from "@bkry/bowline-components/Balance";
import { BowlineBrandLabel } from "@bkry/bowline-components/BowlineBrandLabel";
import { OpenSeaIcon } from "@bkry/bowline-components/Icon";
import { NetworkLabel } from "@bkry/bowline-components/NetworkLabel";
import { SignInWithEthereumButton } from "@bkry/bowline-components/SignInWithEthereumButton";
import { SignInWithSolanaButton } from "@bkry/bowline-components/SignInWithSolanaButton";
import { GenericErrorModal } from "@bkry/bowline-components/StatusModals";
import { TransactionRequest } from "@bkry/bowline-components/TransactionRequest";
import { TrunkatedAddress } from "@bkry/bowline-components/Utils";
import { WalletExport } from "@bkry/bowline-components/WalletExport";
import { utils } from "@bkry/bowline-connectors";
import { getProjectDesignAttributes } from "@bkry/bowline-redux/projectDesigns";
import {
  getProjectAttributes,
  getProjectRelationships,
  projectId,
} from "@bkry/bowline-redux/projects";
import {
  signRequestFail,
  signRequestSuccess,
  signRequests,
} from "@bkry/bowline-redux/signRequests";
import { transactionRequests } from "@bkry/bowline-redux/transactionRequests";
import {
  prepareEmailUpdateWallet,
  siweLogout,
  siweErrorReset,
} from "@bkry/bowline-redux/wallets";
import { getEnv } from "@bkry/bowline-utils";
import PropTypes from "prop-types";
import React from "react";
import { useSsrEffect, useSsrState } from "@issr/core";
import CopyToClipboard from "react-copy-to-clipboard";
import { useForm } from "react-hook-form";
import { AiFillCheckCircle, AiFillCopy } from "react-icons/ai";
import { FaCaretDown, FaCaretUp, FaWallet } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { MdCheckCircle, MdError } from "react-icons/md";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import { chains } from "@bkry/bowline-connectors";
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import WalletTokens from "./utils/WalletTokens";
import { useAccount } from "../CustomHook";

/**
 * Stateless react component to render the BowlineWallet component
 * @param {Object} props the props passed to the component.
 */
const BowlineWallet = (props, context) => {
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  const {
    ethConnector: connector,
    ethProvider: provider,
    ethChainId: chainId,
    ethNetworkChange,
    account,
    isEth,
    isSol,
    solanaWallet,
    solanaChainId,
  } = useAccount();
  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );

  const projectRelationships = useSelector((state) =>
    getProjectRelationships(state, REACT_APP_PROJECT_ID)
  );

  const projectDesignData = useSelector((state) =>
    getProjectDesignAttributes(
      state,
      projectRelationships?.project_design?.data?.id
    )
  );

  const suggestedChainOptions = useSelector(
    (state) => state.projects.defaultNetworks[REACT_APP_PROJECT_ID]
  );

  const signedInWithEthereum = useSelector(
    (state) => state.wallets.siweSuccess
  );

  const signedInWithSolana = useSelector((state) => state.wallets.siwsSuccess);

  const signedIn = signedInWithEthereum || signedInWithSolana;
  const connectorName = utils.getName(connector);
  const userInfo = connector ? connector.userInfo : null;
  const dispatch = useDispatch();
  // const [pendingSignature, setPendingSignature] = useSsrState(false);
  const signaturesList = useSelector((state) => state.signRequests.list);
  const transactionsList = useSelector(transactionRequests);
  const pendingSignatures = useSelector((state) => signRequests(state));
  const pendingSignature =
    pendingSignatures && pendingSignatures.length > 0 && pendingSignatures[0];

  const { handleSubmit, register, reset } = useForm();

  const chainIdToNetwork = {
    1: "ethereum",
    3: "ropsten",
    4: "rinkeby",
    5: "goerli",
    42: "kovan",
    11155111: "sepolia",
    137: "polygon",
    80001: "mumbai",
    100: "lukso",
    1007: "lukso_testnet",
    80002: "amoy",
    1000000001: "solana",
    1000000002: "solana_testnet",
  };
  let chainName = chainIdToNetwork[chainId];
  if (isSol) {
    chainName = chainIdToNetwork[solanaChainId];
  }

  const suggestNetworkChange =
    (isEth &&
      suggestedChainOptions?.chain_id &&
      suggestedChainOptions?.chain_id !== chainId) ||
    (isSol &&
      suggestedChainOptions?.chain_id &&
      suggestedChainOptions?.chain_id !== solanaChainId);

  const currentProjectId = useSelector((state) => projectId(state));

  const [showWallet, setShowWallet] = useSsrState(
    pendingSignatures?.length > 0
  );
  const toggleWallet = () => setShowWallet(!showWallet);
  const siweError = useSelector((state) => state.wallets.siweError);

  const [activeTab, setActiveTab] = useSsrState(isEth ? "0" : "1");

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const [showAccountDeletion, setShowAccountDeletion] = useSsrState(false);
  const toggleAccountDeletion = () =>
    setShowAccountDeletion(!showAccountDeletion);

  const [showEmailChange, setShowEmailChange] = useSsrState(false);
  const toggleEmailChange = () => setShowEmailChange(!showEmailChange);
  const { ref, ...registerEmail } = register("email");

  const onSubmit = (data) => {
    dispatch(
      prepareEmailUpdateWallet({
        provider,
        connector,
        account,
        email: data.email,
        projectId: currentProjectId,
        isEth,
        isSol,
      })
    );

    reset();
  };

  useSsrEffect(() => {
    if (pendingSignature && !showWallet) {
      setShowWallet(true);
    }
  }, [pendingSignature]);

  const handleSignature = async () => {
    if (pendingSignature) {
      try {
        const signer = provider.getSigner();
        const account = await signer.getAddress();

        const signature = await signer.signMessage(pendingSignature.message);
        dispatch(
          pendingSignature.successAction({
            signature,
            account: account || signer,
          })
        );
        pendingSignature.callbackSuccess &&
          pendingSignature.callbackSuccess(signature);
        dispatch(signRequestSuccess(pendingSignature.id));
        toggleWallet();
      } catch (error) {
        dispatch(pendingSignature.failureAction(error));
      }
    }
  };

  const handleCancel = () => {
    if (pendingSignature) {
      dispatch(signRequestFail(pendingSignature.id));
      toggleWallet();
    }
  };

  const handleDisconnect = () => {
    if (isEth) {
      if (connector?.deactivate) {
        connector.deactivate();
      } else {
        connector.resetState();
      }
    }
    if (isSol) {
      solanaWallet?.disconnect();
    }
    dispatch(siweLogout());
  };

  const handleNetworkChange = async (chainHex, chainId) => {
    await ethNetworkChange(chainHex, chainId);
  };

  return (
    <>
      <Button
        onClick={toggleWallet}
        color="primary"
        size="sm"
        className="position-relative rounded-circle ms-2"
      >
        <FaWallet />
        <span className="sr-only">{context.t("Wallet")}</span>
        {!!pendingSignature && (
          <span className="position-absolute top-0 start-100 translate-middle p-2 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">{context.t("New alerts")}</span>
          </span>
        )}
        {!signedIn && (
          <span className="position-absolute top-0 start-100 translate-middle p-2 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">
              {context.t("Not signed in with Wallet")}
            </span>
          </span>
        )}
        {signedIn && suggestNetworkChange && (
          <span className="position-absolute top-0 start-100 translate-middle p-2 bg-warning border border-light rounded-circle">
            <span className="visually-hidden">
              {context.t("Connected to wrong network")}
            </span>
          </span>
        )}
      </Button>
      <Modal
        isOpen={showWallet}
        toggle={toggleWallet}
        className="bowlinewallet__modal"
        fullscreen="md"
        scrollable
      >
        <ModalHeader toggle={toggleWallet} className="text-light bg-dark">
          <div className="d-flex flex-column">
            {projectDesignData?.bowline_branding
              ? "Bowline"
              : projectData?.title}{" "}
            {context.t("Wallet")}
            <span style={{ fontSize: "0.6rem" }}>
              {projectData?.bowline_connect_mode !== "disabled" && (
                <BowlineBrandLabel isConnect={true} />
              )}
            </span>
          </div>
        </ModalHeader>
        {pendingSignature ? (
          <>
            <ModalBody className="d-flex flex-column text-light bg-dark">
              <div className="d-flex justify-content-between my-3">
                <div>
                  {context.t("Account")}:<br />
                  <TrunkatedAddress address={account} />
                </div>
                <div className="text-end">
                  {context.t("Balance")}:<br />
                  {/* <span>0 ETH</span> */}
                  <Balance />
                </div>
              </div>
              <div className="d-flex flex-column flex-grow-1">
                <hr />
                <p className="h4">{context.t("Signing Request")}</p>
                {pendingSignature?.messagePreview && (
                  <div>
                    <p>{pendingSignature?.messagePreview.headline}</p>
                    <p>{pendingSignature?.messagePreview.body}</p>
                    {pendingSignature?.messagePreview?.terms?.map(
                      (term, index) => (
                        <p key={`term-${index}`}>{term}</p>
                      )
                    )}
                    <small>{pendingSignature?.messagePreview.date}</small>
                  </div>
                )}
                {pendingSignature &&
                  !pendingSignature?.messagePreview &&
                  pendingSignature.message && (
                    <p className="overflow-scroll">
                      {pendingSignature.message}
                    </p>
                  )}
              </div>
            </ModalBody>
            <ModalFooter className="bg-dark">
              <div className="flex-grow-1 m-0">
                <Row>
                  <Col>
                    <Button
                      color="secondary"
                      className="w-100"
                      onClick={handleCancel}
                    >
                      {context.t("Cancel")}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={handleSignature}
                    >
                      {context.t("Sign")}
                    </Button>
                  </Col>
                </Row>
              </div>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalBody className="text-light d-flex flex-column bg-dark">
              {!signedIn ? (
                <Alert color="dark">
                  <p className="fw-bold">
                    {context.t("You are not signed in with your Wallet yet")}
                  </p>
                  <p className="small">
                    {context.t(
                      "You are asked to sign a message to generate a secure login connection for your wallet. We won't have access to your funds, NFTs or perform any actions with this information."
                    )}
                  </p>

                  {isEth && (
                    <SignInWithEthereumButton
                      color="primary"
                      block
                      size="sm"
                      onSuccess={toggleWallet}
                    />
                  )}
                  {isSol && (
                    <SignInWithSolanaButton
                      color="primary"
                      block
                      size="sm"
                      onSuccess={toggleWallet}
                    />
                  )}
                </Alert>
              ) : (
                <div>
                  <MdCheckCircle className="text-success me-2" />
                  {context.t("You are signed in with your Wallet")}
                </div>
              )}
              {chainName && (
                <div className="d-flex align-items-center my-2 small">
                  <NetworkLabel network={chainName} />
                </div>
              )}
              {suggestNetworkChange && (
                <div className="d-flex align-items-center my-2 small">
                  <Alert color="dark" className="text-center">
                    <p className="small">
                      {context.t(
                        "You are currently connected to a different network than this contract is deployed on. Please change your network to {networkName} in your wallet.",
                        { networkName: suggestedChainOptions?.chain_name }
                      )}
                    </p>
                    <div className="d-flex">
                      <Button
                        className="ms-auto"
                        color="primary"
                        onClick={() =>
                          handleNetworkChange(
                            suggestedChainOptions?.chain_hex,
                            suggestedChainOptions?.chain_id
                          )
                        }
                      >
                        {context.t("Switch to {networkName} network", {
                          networkName: suggestedChainOptions?.chain_name,
                        })}
                      </Button>
                    </div>
                  </Alert>
                </div>
              )}
              {userInfo && (
                <div>
                  <div className="d-flex align-items-center my-2 small">
                    {userInfo.profileImage && (
                      <img
                        src={userInfo.profileImage}
                        className="img-fluid me-2 rounded"
                        width="20"
                      />
                    )}
                    {userInfo.email && <span>{userInfo.email}</span>}
                    {userInfo.typeOfLogin &&
                      userInfo.typeOfLogin === "google" && (
                        <span className="ms-5 d-flex align-items-center">
                          Logged in <FcGoogle className="ms-1" />
                        </span>
                      )}
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-between my-3">
                <div>
                  {context.t("Account")}:<br />
                  <TrunkatedAddress address={account} />
                  <CopyToClipboard text={account}>
                    <Button
                      color="outline-primary"
                      size="sm"
                      className="ms-2 p-0"
                      id="copyAddressButton"
                    >
                      <AiFillCopy />
                    </Button>
                  </CopyToClipboard>
                  {isEth && (
                    <a
                      href={`https://opensea.io/${account}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", width: "2.5rem" }}
                    >
                      <OpenSeaIcon />
                    </a>
                  )}
                  <UncontrolledTooltip
                    placement="right"
                    className="text-light bg-dark"
                    target="copyAddressButton"
                  >
                    {context.t("Copy address")}
                  </UncontrolledTooltip>
                </div>
                <div className="text-start">
                  {context.t("Balance")}:<br />
                  <Balance />
                </div>
              </div>
              <Nav tabs className="mt-3">
                {isEth && (
                  <NavItem>
                    <NavLink
                      className={
                        activeTab === "0" ? "active" : "cursor-pointer"
                      }
                      onClick={() => toggleTab("0")}
                    >
                      {context.t("Tokens")}
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={activeTab === "1" ? "active" : "cursor-pointer"}
                    onClick={() => toggleTab("1")}
                  >
                    {context.t("Settings")}
                  </NavLink>
                </NavItem>
                {connectorName === "BowlineWallet" && (
                  <NavItem>
                    <NavLink
                      className={
                        activeTab === "2" ? "active" : "cursor-pointer"
                      }
                      onClick={() => toggleTab("2")}
                    >
                      {context.t("Activity")}
                    </NavLink>
                  </NavItem>
                )}
              </Nav>
              <TabContent activeTab={activeTab} className="pt-3">
                <TabPane tabId="0">
                  {signedIn ? (
                    <WalletTokens />
                  ) : (
                    <div className="text-center small my-4">
                      {context.t('Please "Sign in with Wallet" first.')}{" "}
                      {context.t("See above")} &#9757;
                    </div>
                  )}
                </TabPane>
                <TabPane tabId="1">
                  {connectorName === "BowlineWallet" && (
                    <div className="d-flex flex-column flex-grow-1 align-items-center">
                      <WalletExport />
                    </div>
                  )}
                  {signedIn ? (
                    <div className="small my-4">
                      <div>
                        <span
                          className="cursor-pointer mb-3"
                          onClick={toggleEmailChange}
                        >
                          {context.t("Change E-mail address")}{" "}
                          {!showEmailChange && <FaCaretDown />}{" "}
                          {showEmailChange && <FaCaretUp />}
                        </span>
                        <Collapse isOpen={showEmailChange}>
                          <Form
                            className="mb-3"
                            onSubmit={handleSubmit(onSubmit)}
                          >
                            <Input
                              type="email"
                              className="my-3"
                              innerRef={ref}
                              {...registerEmail}
                            />
                            <Button type="submit" color="primary">
                              {context.t("Change")}
                            </Button>
                          </Form>
                        </Collapse>
                      </div>
                      <span
                        className="text-danger cursor-pointer mb-3"
                        onClick={toggleAccountDeletion}
                      >
                        {context.t("Delete Account")}{" "}
                        {!showAccountDeletion && <FaCaretDown />}{" "}
                        {showAccountDeletion && <FaCaretUp />}
                      </span>
                      <Collapse isOpen={showAccountDeletion}>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: context.t(
                              "To delete your account please contact us: <a href='mailto:support@bowline.app' target='_blank'>support@bowline.app</a>."
                            ),
                          }}
                        />
                      </Collapse>
                    </div>
                  ) : (
                    <div className="text-center small my-4">
                      {context.t('Please "Sign in with Wallet" first.')}{" "}
                      {context.t("See above")} &#9757;
                    </div>
                  )}
                  <p className="small">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: context.t(
                          "For more information on the processing of your personal data, please see our <a href='https://www.bowline.app/data-protection/' target='_blank'>Privacy Policy</a>."
                        ),
                      }}
                    />{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: context.t(
                          "Bowline <a href='https://www.bowline.app/tos/' target='_blank'>Terms of Service</a>."
                        ),
                      }}
                    />
                  </p>
                </TabPane>
                <TabPane tabId="2">
                  {transactionsList.length + signaturesList.length === 0 && (
                    <p className="small my-5">
                      {context.t("No Activity yet in this session")}
                    </p>
                  )}
                  {signaturesList.length > 0 && (
                    <>
                      <h5 className="mt-4">{context.t("Recent Signatures")}</h5>
                      {signaturesList
                        .map((signature) => (
                          <div
                            key={signature.id}
                            className="d-flex justify-content-between my-2 border border-primary p-2 rounded"
                          >
                            <span>{signature.signaturePreview}</span>
                            <span>
                              <Moment fromNow>{signature.createdAt}</Moment>
                            </span>
                            {signature?.signRequestStatus === "success" ? (
                              <Badge size="sm" color="primary" className="ms-3">
                                <AiFillCheckCircle />
                              </Badge>
                            ) : (
                              <Badge size="sm" color="info" className="ms-3">
                                <MdError />
                              </Badge>
                            )}

                            {/* <span>{signature.createdAt}</span> */}
                          </div>
                        ))
                        .reverse()}
                    </>
                  )}
                  {transactionsList.length > 0 && (
                    <>
                      <h5 className="mt-4">{context.t("Transactions")}</h5>
                      {transactionsList
                        .map((transaction) => (
                          <TransactionRequest
                            transaction={transaction}
                            key={transaction.id}
                          />
                        ))
                        .reverse()}
                    </>
                  )}
                </TabPane>
              </TabContent>
            </ModalBody>

            <ModalFooter className="bg-dark">
              <div className="flex-grow-1 m-0 bg">
                <Row>
                  <Col>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={handleDisconnect}
                    >
                      {context.t("Disconnect")}
                    </Button>
                  </Col>
                </Row>
              </div>
            </ModalFooter>
          </>
        )}
      </Modal>

      <GenericErrorModal
        error={!!siweError?.attributes?.full_error_message}
        errorReset={siweErrorReset}
        rawError={siweError?.attributes?.full_error_message}
        primaryAction={{
          title: context.t("ok"),
          action: siweErrorReset,
        }}
      />
    </>
  );
};

/**  define proptype for the 'translation' function  */
BowlineWallet.contextTypes = {
  t: PropTypes.func,
};

BowlineWallet.propTypes = {};

export default BowlineWallet;
